import { motion } from "framer-motion";
import { Sparkles, Brain, Music, Wand2, Zap, MessageSquare, Shield, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import aiHeroImage from "@/assets/ai-taste-builder-hero.jpg";

const features = [
  {
    icon: Brain,
    title: "Cultural Intelligence",
    description: "Analyzes your event's cultural context for the perfect traditional-contemporary blend.",
  },
  {
    icon: Music,
    title: "Vibe Matching",
    description: "Share your playlist—our AI curates instruments that capture your unique sound.",
  },
  {
    icon: Wand2,
    title: "Smart Packages",
    description: "AI-generated musician packages tailored to your budget and venue.",
  },
  {
    icon: MessageSquare,
    title: "Natural Booking",
    description: "Describe your vision in plain English—we translate it into perfect music.",
  },
];

const benefits = [
  "Instant personalized recommendations",
  "Personal consultation to match you with the perfect musicians",
  "Transparent pricing upfront",
  "Cultural expertise built-in",
];

const AITasteBuilderSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="ai-section" className="relative py-16 sm:py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-background via-background to-muted/30">
      {/* Animated background elements - reduced on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-primary/10 rounded-full blur-3xl animate-pulse [animation-delay:1s] hidden sm:block" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-16"
        >
          {/* Patent Pending Badge */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Badge variant="outline" className="px-3 py-1.5 sm:px-4 sm:py-2 bg-primary/10 border-primary/30 text-primary text-xs sm:text-sm">
              <Sparkles className="w-3 h-3 mr-1" />
              AI-Powered
            </Badge>
            <Badge variant="outline" className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500/10 border-yellow-500/30 text-yellow-700 dark:text-yellow-400 text-xs sm:text-sm">
              <Shield className="w-3 h-3 mr-1" />
              Patent Pending
            </Badge>
          </div>
          
          <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 px-2">
            The Future of Event Music <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
              Is Already Here
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
            Our proprietary <strong className="text-foreground">Taste Builder™</strong> technology understands your vision 
            better than traditional booking ever could. Share your music taste, and watch as AI curates the perfect live experience.
          </p>
        </motion.div>

        {/* Hero Image with Overlay */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative mb-12 sm:mb-20 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl shadow-primary/10"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent z-10" />
          <img
            src={aiHeroImage}
            alt="AI-powered music curation interface"
            className="w-full h-[280px] sm:h-[400px] lg:h-[500px] object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 z-20">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
              <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-background/90 backdrop-blur-sm border border-border hover:border-primary/50 transition-colors">
                <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="text-xs sm:text-sm font-medium">Instant Recommendations</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-background/90 backdrop-blur-sm border border-border hover:border-primary/50 transition-colors">
                <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="text-xs sm:text-sm font-medium">Cultural Analysis</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-background/90 backdrop-blur-sm border border-border hover:border-primary/50 transition-colors">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="text-xs sm:text-sm font-medium">2-Minute Setup</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-12 sm:mb-16 items-center">
          {/* Left: Benefits List */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4 sm:space-y-6"
          >
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold px-2">
              Why Event Planners Are Switching to AI-Powered Booking
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground px-2">
              Traditional music booking is broken. Endless emails, unclear pricing, and musicians who don't 
              understand your vision. The Taste Builder changes everything.
            </p>
            <ul className="space-y-3 sm:space-y-4 px-2">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={benefit}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/20 flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                  </div>
                  <span className="text-sm sm:text-base font-medium">{benefit}</span>
                </motion.li>
              ))}
            </ul>
            <div className="pt-2 sm:pt-4 px-2">
              <Button
                size="lg"
                onClick={scrollToContact}
                className="w-full sm:w-auto shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 active:scale-95 sm:hover:scale-105 transition-all duration-300"
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Try the Taste Builder Free
              </Button>
            </div>
          </motion.div>

          {/* Right: Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-card border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 lg:hover:-translate-y-1"
              >
                <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative z-10">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  </div>
                  <h4 className="text-sm sm:text-base font-semibold mb-1 text-foreground">{feature.title}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center p-6 sm:p-8 rounded-xl sm:rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border border-primary/20"
        >
          <h3 className="text-xl sm:text-2xl font-bold mb-2">Ready to Transform Your Event?</h3>
          <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6 max-w-xl mx-auto px-2">
            Join hundreds of event planners who've discovered a better way to book live music. 
            Your guests will thank you.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <Button
              size="lg"
              onClick={scrollToContact}
              className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 active:scale-95 sm:hover:scale-105"
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Get Your Free Quote
            </Button>
          </div>
          <p className="mt-4 text-xs sm:text-sm text-muted-foreground flex flex-wrap justify-center gap-x-2 gap-y-1">
            <span>✓ No credit card required</span>
            <span className="hidden sm:inline">•</span>
            <span>✓ Response within 24 hours</span>
            <span className="hidden sm:inline">•</span>
            <span>✓ Zero obligation</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AITasteBuilderSection;
